/**
 * RolesTab Component (H.17)
 * 
 * Tab content for managing platform roles.
 * Shows list of roles with expandable cards for module permissions.
 * Includes modals for creating roles and managing members.
 */

import { useState } from 'react';
import { DiscardChangesDialog } from '@vanguard-atlas/shared';
import { useI18n } from '@/i18n';
import { 
  Button, 
  Badge, 
  Skeleton,
  Icon,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { SimpleSelect } from '@/components/ui/form-wrappers';
import { Shield, User, X, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { 
  useRoles, 
  useRoleMembers, 
  useCreateRole, 
  useAddRoleMember, 
  useRemoveRoleMember,
  useDeleteRole,
  useTenantMembers,
  useModules,
} from '@/features/permissions/hooks';
import type { Role, RoleMember } from '@/features/permissions/types';
import {
  getModuleLabelKey,
  humanizeModuleName,
  MODULE_ICON_MAP,
} from '@/features/permissions/moduleMetadata';

// =============================================================================
// Constants
// =============================================================================

const PERMISSION_LEVELS = ['admin', 'edit', 'operate', 'use', 'view'];

interface ModuleItem {
  id: string;
  name: string;
  description?: string;
}

function getModuleLabel(t: (key: string) => string, moduleName: string): string {
  const labelKey = getModuleLabelKey(moduleName);
  if (!labelKey) return humanizeModuleName(moduleName);
  const translated = t(labelKey);
  return translated === labelKey ? humanizeModuleName(moduleName) : translated;
}

const LEVEL_COLORS: Record<string, string> = {
  admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  edit: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  operate: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  use: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  view: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
};

// =============================================================================
// Module Permissions Matrix
// =============================================================================

function ModulePermissionsMatrix({
  permissions,
  modules,
}: {
  permissions: Record<string, string>;
  modules: ModuleItem[];
}) {
  const { t } = useI18n();
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
      {modules.map((mod) => {
        const level = permissions[mod.name];
        if (!level) return null;
        const ModIcon = MODULE_ICON_MAP[mod.name];
        return (
          <div
            key={mod.id}
            className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700"
          >
            <span className="text-sm flex items-center gap-1.5">
              {ModIcon ? <ModIcon className="h-3.5 w-3.5" /> : null}
              <span>{getModuleLabel(t, mod.name)}</span>
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${LEVEL_COLORS[level] || LEVEL_COLORS.view}`}>
              {t(`admin.permissions.levels.${level}`)}
            </span>
          </div>
        );
      })}
      {Object.keys(permissions).length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400 col-span-full">
          {t('admin.permissions.roles.noModulePermissions')}
        </p>
      )}
    </div>
  );
}

// =============================================================================
// Create Role Dialog
// =============================================================================

interface CreateRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function CreateRoleDialog({ open, onOpenChange }: CreateRoleDialogProps) {
  const { t } = useI18n();
  const createRole = useCreateRole();
  const { data: modulesResponse, isLoading: loadingModules } = useModules();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [modulePerms, setModulePerms] = useState<Record<string, string>>({});
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const modules = modulesResponse?.items || [];

  // ADR-114 enmienda 1: dirty = diff vs estado inicial (formulario vacío).
  const isDirty =
    open &&
    (name.trim() !== '' ||
      description.trim() !== '' ||
      Object.keys(modulePerms).length > 0);

  const discardLabels = {
    title: t('common.discardChanges.title') ?? 'Discard changes?',
    message:
      t('common.discardChanges.message') ??
      'You have unsaved changes. If you close now, they will be lost.',
    confirm: t('common.discardChanges.discard') ?? 'Discard',
    cancel: t('common.discardChanges.keepEditing') ?? 'Keep editing',
  };

  const requestClose = () => {
    if (isDirty) {
      setConfirmDiscardOpen(true);
      return;
    }
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;
    try {
      await createRole.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        module_permissions: modulePerms,
      });
      toast.success(t('admin.permissions.roles.createSuccess') || `Role "${name}" created`);
      setName('');
      setDescription('');
      setModulePerms({});
      onOpenChange(false);
    } catch {
      toast.error(t('admin.permissions.roles.createError') || 'Failed to create role');
    }
  };

  const handleModuleChange = (moduleId: string, level: string) => {
    setModulePerms(prev => {
      if (!level || level === '__none__') {
        const rest = { ...prev };
        delete rest[moduleId];
        return rest;
      }
      return { ...prev, [moduleId]: level };
    });
  };

  const levelOptions = [
    { value: '__none__', label: '— None —' },
    ...PERMISSION_LEVELS.map(l => ({ value: l, label: t(`admin.permissions.levels.${l}`) })),
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          requestClose();
          return;
        }
        onOpenChange(v);
      }}
    >
      <DialogContent
        className="sm:max-w-lg flex flex-col"
        dirty={isDirty}
        discardTitle={discardLabels.title}
        discardMessage={discardLabels.message}
        discardConfirmLabel={discardLabels.confirm}
        discardCancelLabel={discardLabels.cancel}
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            {t('admin.permissions.roles.createTitle') || 'Create Custom Role'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4 overflow-y-auto min-h-0 flex-1">
          <div className="space-y-2">
            <Label htmlFor="role-name">{t('admin.permissions.roles.name') || 'Role Name'}</Label>
            <Input
              id="role-name"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. noc-specialist"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="role-desc">{t('admin.permissions.roles.descriptionLabel') || 'Description'}</Label>
            <Textarea
              id="role-desc"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Describe the role's purpose"
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('admin.permissions.roles.modulePermissions') || 'Module Permissions'}</Label>
            <div className="space-y-2">
              {modules.map((mod) => {
                const ModIcon = MODULE_ICON_MAP[mod.name];
                return (
                  <div key={mod.id} className="flex items-center justify-between gap-4">
                    <span className="text-sm flex items-center gap-1.5 min-w-[140px]">
                      {ModIcon ? <ModIcon className="h-3.5 w-3.5" /> : null} {getModuleLabel(t, mod.name)}
                    </span>
                    <SimpleSelect
                      options={levelOptions}
                      value={modulePerms[mod.name] || ''}
                      onValueChange={v => handleModuleChange(mod.name, v)}
                      className="w-32"
                    />
                  </div>
                );
              })}
              {!loadingModules && modules.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No modules available.
                </p>
              )}
            </div>
          </div>
        </div>
        <DialogFooter className="shrink-0">
          <Button variant="outline" onClick={requestClose}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!name.trim() || createRole.isPending || loadingModules}
          >
            {createRole.isPending ? (
              <><Icon name="loader" size={16} className="animate-spin mr-1" /> Creating...</>
            ) : (
              t('common.create') || 'Create'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* ADR-114 enmienda 1: confirmación de descarte */}
      <DiscardChangesDialog
        open={confirmDiscardOpen}
        onDiscard={() => {
          setConfirmDiscardOpen(false);
          setName('');
          setDescription('');
          setModulePerms({});
          onOpenChange(false);
        }}
        onCancel={() => setConfirmDiscardOpen(false)}
        title={discardLabels.title}
        message={discardLabels.message}
        confirmLabel={discardLabels.confirm}
        cancelLabel={discardLabels.cancel}
      />
    </Dialog>
  );
}

// =============================================================================
// Add Role Member Dialog
// =============================================================================

interface AddRoleMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roleId: string;
  roleName: string;
}

function AddRoleMemberDialog({ open, onOpenChange, roleId, roleName }: AddRoleMemberDialogProps) {
  const { t } = useI18n();
  const addMember = useAddRoleMember();
  const { data: tenantMembers, isLoading } = useTenantMembers();
  const [selectedUserId, setSelectedUserId] = useState('');

  const memberOptions = (tenantMembers?.items || []).map(m => ({
    value: m.user_id,
    label: m.name || m.email || m.user_id,
  }));

  const handleSubmit = async () => {
    if (!selectedUserId) return;
    try {
      await addMember.mutateAsync({ roleId, userId: selectedUserId });
      toast.success(t('admin.permissions.roles.memberAdded') || 'Member added to role');
      setSelectedUserId('');
      onOpenChange(false);
    } catch {
      toast.error(t('admin.permissions.roles.memberAddError') || 'Failed to add member');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {t('admin.permissions.roles.addMemberTitle') || `Add Member to "${roleName}"`}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <SimpleSelect
            label={t('admin.permissions.roles.selectUser') || 'Select User'}
            options={memberOptions}
            value={selectedUserId}
            onValueChange={setSelectedUserId}
            placeholder="Choose a user..."
            disabled={isLoading}
            required
          />
        </div>
        <DialogFooter className="shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!selectedUserId || addMember.isPending}
          >
            {addMember.isPending ? (
              <><Icon name="loader" size={16} className="animate-spin mr-1" /> Adding...</>
            ) : (
              t('common.add') || 'Add'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Role Card
// =============================================================================

interface RoleCardProps {
  role: Role;
  modules: ModuleItem[];
  isExpanded: boolean;
  onToggle: () => void;
}

function RoleCard({ role, modules, isExpanded, onToggle }: RoleCardProps) {
  const { t } = useI18n();
  const { data: members, isLoading: loadingMembers } = useRoleMembers(role.id, { enabled: isExpanded });
  const removeMember = useRemoveRoleMember();
  const deleteRole = useDeleteRole();
  const [showAddMember, setShowAddMember] = useState(false);

  const handleRemoveMember = async (member: RoleMember) => {
    try {
      await removeMember.mutateAsync({ roleId: role.id, userId: member.user_id });
      toast.success(t('admin.permissions.roles.memberRemoved') || 'Member removed from role');
    } catch {
      toast.error(t('admin.permissions.roles.memberRemoveError') || 'Failed to remove member');
    }
  };

  const handleDeleteRole = async () => {
    if (role.type === 'system') return;
    try {
      await deleteRole.mutateAsync(role.id);
      toast.success(t('admin.permissions.roles.deleteSuccess') || `Role "${role.name}" deleted`);
    } catch {
      toast.error(t('admin.permissions.roles.deleteError') || 'Failed to delete role');
    }
  };

  return (
    <>
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        {/* Role Header */}
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-between p-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Shield className="h-6 w-6 text-gray-500 dark:text-gray-400" />
            <div className="text-left">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {role.name}
                </span>
                {role.type === 'system' && (
                  <Badge variant="secondary" className="text-xs">
                    {t('admin.permissions.roles.system') || 'System'}
                  </Badge>
                )}
              </div>
              {role.description && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {role.description}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {role.member_count} {t('admin.permissions.roles.members') || 'members'}
            </span>
            <span className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
              <ChevronDown className="h-4 w-4" />
            </span>
          </div>
        </button>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800/50 space-y-5">
            {/* Members Section */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900 dark:text-gray-100">
                  {t('admin.permissions.roles.members') || 'Members'}
                </h4>
                <Button 
                  variant="outline" 
                  size="sm" 
                  leftIcon="plus"
                  onClick={() => setShowAddMember(true)}
                >
                  {t('admin.permissions.roles.addMember') || 'Add Member'}
                </Button>
              </div>
              {loadingMembers ? (
                <div className="space-y-2">
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-full" />
                </div>
              ) : members && members.length > 0 ? (
                <div className="space-y-2">
                  {members.map((member) => (
                    <div
                      key={member.user_id}
                      className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700"
                    >
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-500" />
                        <span className="text-sm">{member.name || member.user_id}</span>
                        {member.email && (
                          <span className="text-xs text-gray-500">({member.email})</span>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                        onClick={() => handleRemoveMember(member)}
                        disabled={removeMember.isPending}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('admin.permissions.roles.noMembers') || 'No members assigned'}
                </p>
              )}
            </div>

            {/* Module Permissions Matrix */}
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                {t('admin.permissions.roles.modulePermissions') || 'Module Permissions'}
              </h4>
              <ModulePermissionsMatrix permissions={role.module_permissions} modules={modules} />
            </div>

            {/* Delete button for custom roles */}
            {role.type === 'custom' && (
              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 border-red-300 hover:bg-red-50 dark:border-red-700 dark:hover:bg-red-900/20"
                  onClick={handleDeleteRole}
                  disabled={deleteRole.isPending}
                >
                  {t('admin.permissions.roles.delete') || 'Delete Role'}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Member Dialog */}
      <AddRoleMemberDialog
        open={showAddMember}
        onOpenChange={setShowAddMember}
        roleId={role.id}
        roleName={role.name}
      />
    </>
  );
}

// =============================================================================
// RolesTab
// =============================================================================

export function RolesTab() {
  const { t } = useI18n();
  const { data: roles, isLoading, error } = useRoles();
  const { data: modulesResponse } = useModules();
  const [expandedRole, setExpandedRole] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const modules = modulesResponse?.items || [];

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        {t('admin.permissions.roles.error') || 'Failed to load roles'}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {/* Header with Add button */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('admin.permissions.roles.description') || 
              'Roles define platform-wide permission templates. System roles cannot be deleted.'}
          </p>
          <Button 
            variant="outline" 
            size="sm" 
            leftIcon="plus"
            onClick={() => setShowCreateDialog(true)}
          >
            {t('admin.permissions.roles.addCustom') || 'New Role'}
          </Button>
        </div>

        {/* Roles List */}
        <div className="space-y-3">
          {roles?.items?.map((role) => (
            <RoleCard
              key={role.id}
              role={role}
              modules={modules}
              isExpanded={expandedRole === role.id}
              onToggle={() => setExpandedRole(expandedRole === role.id ? null : role.id)}
            />
          ))}
        </div>
      </div>

      {/* Create Role Dialog */}
      <CreateRoleDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />
    </>
  );
}

export default RolesTab;
